'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'

import { useAuth } from '@/hooks/use-auth'
import { fitsPlanLimit, normalizePlanLimit } from '@/lib/billing/utils/plan-limits'

interface UseShopifyDomainUsageState {
  count: number
  /** null = unlimited */
  limit: number | null
  domains: string[]
  loading: boolean
  /**
   * Local check used by `ShopDomainField` to short-circuit a 403 round-trip
   * when the admin types a new domain at the limit. Server-side
   * `checkShopifyStoreLimit` remains the authoritative gate — this is just
   * a UX shortcut. Idempotent (already-existing) candidates are always
   * allowed; brand-new candidates are allowed only when there's headroom.
   */
  allowedToAdd: (candidate: string) => boolean
  refresh: () => Promise<void>
}

const INITIAL: Omit<UseShopifyDomainUsageState, 'allowedToAdd' | 'refresh'> = {
  count: 0,
  limit: 0,
  domains: [],
  loading: true,
}

export function useShopifyDomainUsage(): UseShopifyDomainUsageState {
  const { fetchWithAuth } = useAuth()
  const [state, setState] = useState(INITIAL)

  const refresh = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/api/billing/shopify-domain-usage')
      if (!res.ok) {
        setState({ count: 0, limit: 0, domains: [], loading: false })
        return
      }
      const data = (await res.json()) as {
        currentCount?: number
        limit?: number | null
        domainsInUse?: string[]
      }
      setState({
        count: typeof data.currentCount === 'number' ? data.currentCount : 0,
        limit: normalizePlanLimit(data.limit),
        domains: Array.isArray(data.domainsInUse) ? data.domainsInUse : [],
        loading: false,
      })
    } catch {
      setState({ count: 0, limit: 0, domains: [], loading: false })
    }
  }, [fetchWithAuth])

  useEffect(() => {
    refresh()
  }, [refresh])

  // Pre-compute the lowercased Set so allowedToAdd is O(1) per call.
  const domainSet = useMemo(() => {
    return new Set(state.domains.map((d) => d.trim().toLowerCase()))
  }, [state.domains])

  const allowedToAdd = useCallback(
    (candidate: string): boolean => {
      const normalized = candidate.trim().toLowerCase()
      if (!normalized) return false
      // Idempotent: candidate is already counted, no growth
      if (domainSet.has(normalized)) return true
      // New addition: only allowed if there's headroom
      return fitsPlanLimit(state.count + 1, state.limit)
    },
    [domainSet, state.count, state.limit],
  )

  return {
    count: state.count,
    limit: state.limit,
    domains: state.domains,
    loading: state.loading,
    allowedToAdd,
    refresh,
  }
}
